import { BaseViewStore, BaasicDropdownStore } from "core/stores";
import { action, observable, runInAction } from "mobx";
import { toUTCDate, uuid, getErrorMessage } from "core/utils";
import { ModalParams } from 'core/models';
import { NikoPatientDetailService, NikoPatientTemplateService, NikoPatientTemplateItemService, NikoPatientService } from "application/niko-patient/services";
import { FacilityService } from 'application/facility/services';
import { EligibilityStatusService } from 'application/eligibility-status/services';
import { LookupService } from "common/services";
import { ItemSubcategoryService } from "application/facility-order/services";
import { DiagnosisCodeService, LocalDoctorService } from "application/import/services";
import { ClinicalStatusService } from "application/clinical-status/services";
import { MissingDocumentService } from "application/missing-document/services";
import { HistoryService } from "application/history/services";
class NikoPatientPreviewViewStore extends BaseViewStore {
	@observable accessor nikoPatientDetail;
	@observable accessor disabled = false;
	@observable accessor showInsuranceInfo = true;
	@observable accessor insuranceInfoNote;
	@observable accessor selectedTab = 'Templates';
	@observable accessor isFacilityEditMode = false;
	@observable accessor tempFacility = null;
	@observable accessor refreshKey = uuid();
	@observable accessor patientInfoKey = uuid();

	statuses = [{ name: "Hospital" }, { name: "Discharged" }, { name: "Expired" }, { name: "Active" }];

	tabItems = [
		{ id: 'Templates', name: 'Templates', disabled: false },
		{ id: 'Orders', name: 'Orders', disabled: true },
		{ id: 'History', name: 'History', disabled: true }
	];

	constructor(rootStore) {
        super(rootStore);

        this.rootStore = rootStore;
        this.id = rootStore.routerStore.routerState.params.id;

        //Patient
        this.nikoPatientDetailService = new NikoPatientDetailService(rootStore.application.baasic.apiClient);
        this.facilityService = new FacilityService(rootStore.application.baasic.apiClient);
        this.eligibilityStatusService = new EligibilityStatusService(rootStore.application.baasic.apiClient);
        //Template
        this.patientTemplateService = new NikoPatientTemplateService(rootStore.application.baasic.apiClient);
        this.patientTemplateItemService = new NikoPatientTemplateItemService(rootStore.application.baasic.apiClient);
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);
        //TemplateItem
        this.clinicalStatusService = new ClinicalStatusService(rootStore.application.baasic.apiClient);
        this.missingDocumentService = new MissingDocumentService(this.rootStore.application.baasic.apiClient);
        this.localDoctorService = new LocalDoctorService(rootStore.application.baasic.apiClient);
        this.diagnosisCodeService = new DiagnosisCodeService(rootStore.application.baasic.apiClient);
        this.itemSubcategoryService = new ItemSubcategoryService(rootStore.application.baasic.apiClient);
        this.nikoPatientService = new NikoPatientService(rootStore.application.baasic.apiClient);
        this.historyService = new HistoryService(rootStore.application.baasic.apiClient);

        this.nikoPatientEligibilityStatusNoteModal = new ModalParams({});
        this.nikoPatientInsuranceQuestionModal = new ModalParams({});
        this.nikoPatientStatusModal = new ModalParams({});
        this.nikoPatientInsuranceInfoNoteModal = new ModalParams({ onClose: () => { this.insuranceInfoNote = this.nikoPatientDetail.insuranceInfoNote } });
        this.facilityEditConfirmationModal = new ModalParams({});
        this.nikoFacilityOrderReasonForChangeModal = new ModalParams({});
        //Template
        this.nikoTemplateCreateModal = new ModalParams({});
        this.facilityOrderTemplateNewFlowDeleteModal = new ModalParams({});
        this.nikoTemplateMissingProductNoteModal = new ModalParams({});
        this.facilityOrderTemplateItemNewFlowDeleteQuestionModal = new ModalParams({});
        this.templatePNMissingInformationModal = new ModalParams({ onClose: null });
        this.nikoPatientTemplateItemCalculationModal = new ModalParams({});
        this.patientTemplateDoNotSupplyModal = new ModalParams({});
        //TemplateItem
        this.nikoTemplateMissingDocumentNoteModal = new ModalParams({ onClose: null });
        this.patientTemplateClinicalStatusNoteModal = new ModalParams({});

        this.fetch([this.initialize()]);
    }

	@action.bound
	async initialize() {
		await this.getLookups();
		await this.getNikoPatient();

		this.statusStore = new BaasicDropdownStore({ filterable: false, dataItemKey: 'name' }, {
			fetchFunc: () => {
				return this.statuses;
			},
		});

		this.facilityStore = new BaasicDropdownStore({ filterable: true }, {
			fetchFunc: async (term) => {
				const response = await this.facilityService.find({
					searchQuery: term,
					rpp: 50,
					isNiko: true
				});
				return response.data.item;
			},
		});

		this.eligibleEligibilityStatusId = (await this.eligibilityStatusService.find({ searchQuery: "Eligible", rpp: 1 })).data.item[0].id;

		this.eligibilityStatusStore = new BaasicDropdownStore({ filterable: false }, {
			fetchFunc: async (term) => {
				const response = await this.eligibilityStatusService.find({
					searchQuery: term,
					rpp: 50
				});
				return response.data.item;
			},
		});
	}

	@action.bound
	async getLookups() {
		const response = await this.lookupService.getLookups("SubCategory,SpecialtyType,ImportAction,NewFlowStatus,DefaultItemType");
		runInAction(() => {
			this.subCategories = response.subCategory;
			this.specialtyTypeLookup = response.specialtyType;
			this.insertActionId = response.importAction.find((s) => s.abrv === 'insert').id;
			this.deleteActionId = response.importAction.find((s) => s.abrv === 'delete').id;
			this.updateActionId = response.importAction.find((s) => s.abrv === 'update').id;
			this.noChangeStatusId = response.newFlowStatus.find(s => s.abrv === 'no-change').id;
			this.changeStatusId = response.newFlowStatus.find(s => s.abrv === 'change').id;
			this.dischargeStatusId = response.newFlowStatus.find(s => s.abrv === 'discharge').id;
			this.syringeDefaultItemTypeId = response.defaultItemType.find((s) => s.abrv === 'syringe').id;
		});
	}

	@action.bound
	async getNikoPatient() {
		const response = await this.nikoPatientDetailService.find({ nikoPatientId: this.id, isCopy: false, excludeMasterPatients: true, rpp: 1, embed: "facility,eligibilityStatus,nikoPatient,nikoPatientDetailInsurances" });
        if (response.data && response.data.item && response.data.item.length == 0)
		{
			this.rootStore.notificationStore.error("Access to Master Patient not allowed.");
			this.rootStore.routerStore.goTo('master.app.main.niko-patient.list');
			return;
		}
		var isRefetch = !!this.nikoPatientDetail;
		var nikoPatientDetail = { ...response.data.item[0], nikoPatient: { ...response.data.item[0].nikoPatient, dateOfBirth: toUTCDate(response.data.item[0].nikoPatient.dateOfBirth) }, tempHeight: response.data.item[0].height, tempWeight: response.data.item[0].weight };
		this.createInsuranceCompareModel(nikoPatientDetail);
		nikoPatientDetail.isMasterPatient = nikoPatientDetail.facility && nikoPatientDetail.facility.nikoMasterPatientId == nikoPatientDetail.nikoPatientId;
		nikoPatientDetail.history = { userName: '', eventDate: null, fieldName: '' };
		runInAction(() => {
			this.nikoPatientDetail = nikoPatientDetail;
			if (isRefetch) {
				this.patientInfoKey = uuid();
			}
			this.tempFacility = nikoPatientDetail.facility;
			this.insuranceInfoNote = nikoPatientDetail.insuranceInfoNote;
			this.disabled = nikoPatientDetail.facilityOrderId;
		});
		this.nikoTemplateCreateModal.data.facilityId = this.nikoPatientDetail.facilityId;
	}

	@action.bound
	async toggleRentalsCapped() {
		try {
			const newValue = !this.nikoPatientDetail.nikoPatient.rentalsCapped;
			await this.nikoPatientService.rentalsCapped({ id: this.id, rentalsCapped: newValue });
			runInAction(() => { this.nikoPatientDetail.nikoPatient.rentalsCapped = newValue; });
		} catch (err) {
			this.rootStore.notificationStore.error("Error updating rentals capped.");
		}
	}

	@action.bound
	async toggleHasIvpole() {
		try {
			const newValue = !this.nikoPatientDetail.nikoPatient.hasIvpole;
			await this.nikoPatientService.hasIvpole({ id: this.id, hasIvpole: newValue });
			runInAction(() => { this.nikoPatientDetail.nikoPatient.hasIvpole = newValue; });
		} catch (err) {
			this.rootStore.notificationStore.error("Error updating has IV pole.");
		}
	}

	@action.bound
	createInsuranceCompareModel(nikoPatientDetail) {
		nikoPatientDetail.primaryActive = {
			id: { initial: null, current: null },
			insuranceId: { initial: null, current: null },
			insurance: { initial: null, current: null },
			policyNo: { initial: null, current: null },
			startDate: { initial: null, current: null },
			endDate: { initial: null, current: null },
		};
		nikoPatientDetail.primaryInactive = {
			id: { initial: null, current: null },
			insuranceId: { initial: null, current: null },
			insurance: { initial: null, current: null },
			policyNo: { initial: null, current: null },
			startDate: { initial: null, current: null },
			endDate: { olinitiald: null, current: null },
		};
		nikoPatientDetail.secondaryActive = {
			id: { initial: null, current: null },
			insuranceId: { initial: null, current: null },
			insurance: { initial: null, current: null },
			policyNo: { initial: null, current: null },
			startDate: { initial: null, current: null },
			endDate: { initial: null, current: null },
		};
		nikoPatientDetail.secondaryInactive = {
			id: { initial: null, current: null },
			insuranceId: { initial: null, current: null },
			insurance: { initial: null, current: null },
			policyNo: { initial: null, current: null },
			startDate: { initial: null, current: null },
			endDate: { initial: null, current: null },
		};

		const fill = (list, targetKey) => {
			if (!list) return;
			const map = targetKey === 'current' ?
				{ type: 'type', status: 'status', id: 'id', insuranceId: 'insuranceId', insurance: 'insurance', policyNo: 'policyNo', startDate: 'startDate', endDate: 'endDate' }
				:
				{ type: 'Type', status: 'Status', id: 'Id', insuranceId: 'InsuranceId', insurance: 'Insurance', policyNo: 'PolicyNo', startDate: 'StartDate', endDate: 'EndDate' };

			list.forEach(i => {
				const key = `${i[map.type].toLowerCase()}${i[map.status]}`;
				const target = nikoPatientDetail[key];
				if (!target) return;
				target.id[targetKey] = i[map.id];
				target.insuranceId[targetKey] = i[map.insuranceId];
				target.insurance[targetKey] = i[map.insurance];
				target.policyNo[targetKey] = i[map.policyNo];
				target.startDate[targetKey] = i[map.startDate];
				target.endDate[targetKey] = i[map.endDate];
			});
		};

		fill(nikoPatientDetail.nikoPatientDetailInsurances, 'current');
		if (nikoPatientDetail.initialNikoPatientDetail)
			fill(nikoPatientDetail.initialNikoPatientDetail.NikoPatientDetailInsurances, 'initial');
	}

	@action.bound
	async changePatientStatus(value) {
		this.nikoPatientStatusModal.open({
			status: value.name,
			nikoPatientDetail: this.nikoPatientDetail,
			getNikoPatient: async () => { await this.getNikoPatient(); this.reloadPatientTemplates(); }
		});
	}

	@action.bound
	async changeInsuranceInfoVisibility() {
		this.showInsuranceInfo = !this.showInsuranceInfo;
	}

	@action.bound
	async setTab(tabId) {
		this.selectedTab = tabId;
	}

	@action.bound
	async updateInsuranceInfoNote(value, save = false) {
		this.insuranceInfoNote = value;
		if (save) {
			this.nikoPatientDetail.insuranceInfoNote = this.insuranceInfoNote;
			await this.updateInsuranceInfo();
		}
	}

	@action.bound
	async changeEligibilityStatus(value) {
		this.nikoPatientDetail.eligibilityStatusId = value.id;
		this.nikoPatientDetail.eligibilityStatus = (await this.eligibilityStatusService.find({ rpp: 50 })).data.item.find(e => e.id === value.id);
		await this.updateInsuranceInfo();
		this.reloadPatientTemplates();
	}

	@action.bound
	reloadPatientTemplates() {
		this.refreshKey = uuid();
	}

	@action.bound
	async updateInsuranceInfo() {
		this.disabled = true;
		try {
			await this.nikoPatientDetailService.updatePatientInsuranceInfo(this.nikoPatientDetail);
			const response = await this.nikoPatientDetailService.get(this.nikoPatientDetail.id, { field: "eligibilityStatusNote" });
			this.nikoPatientDetail.eligibilityStatusNote = response.data.eligibilityStatusNote;
		}
		catch (err) {
			this.rootStore.notificationStore.error(getErrorMessage(err, 'Error occurred while trying to update patient Eligibility status.'));
			await this.getNikoPatient();
		}
		finally {
			this.disabled = false;
		}
	}

	@action.bound
	async openNikoPatientEligibilityStatusNoteModal() {
		this.nikoPatientEligibilityStatusNoteModal.open({
			nikoPatientDetail: this.nikoPatientDetail,
		});
	}

	@action.bound
	async openNikoPatientInsuranceQuestionModal() {
		this.nikoPatientInsuranceQuestionModal.open({
			nikoPatientDetail: this.nikoPatientDetail,
		});
	}

	@action.bound
	async openNikoPatientInsuranceInfoNoteModal() {
		this.nikoPatientInsuranceInfoNoteModal.open({
			nikoPatientDetail: this.nikoPatientDetail,
		});
	}

	@action.bound
	changeFacilityEditMode() {
		this.tempFacility = this.nikoPatientDetail.facility;
		this.isFacilityEditMode = !this.isFacilityEditMode;
	}

	@action.bound
	async setFacility(value) {
		this.tempFacility = value;
	}

	@action.bound
	async changeFacility() {
		if (this.tempFacility) {
			await this.facilityEditConfirmationModal.open({
				onConfirm: async () => await this.updatePatientFacility(),
				message: 'Are you sure you want to change this patient’s facility?'
			});
		}
		else {
			this.rootStore.notificationStore.error("Facility must be selected");
		}
	}

	async updatePatientFacility() {
		this.disabled = true;
		try {
			await this.nikoPatientDetailService.updatePatientFacility({
				id: this.nikoPatientDetail.id,
				facilityId: this.tempFacility.id
			});
			this.rootStore.notificationStore.success("Facility successfully updated.");
			this.isFacilityEditMode = false;
		}
		catch (err) {
			const notificationTime = 8000;
			this.rootStore.notificationStore.error(getErrorMessage(err, 'Error occurred while trying to update facility.'), null, notificationTime);
		}
		finally {
			await this.getNikoPatient();
			this.disabled = false;
		}
	}

	@action.bound
	async signalRAction(message) {
		if (message.table === "PM") {
			await this.getNikoPatient();
			this.reloadPatientTemplates();
		}
	}

	@action.bound
	async openReasonForChangeModal() {
		this.nikoFacilityOrderReasonForChangeModal.open({
			nikoPatientDetail: this.nikoPatientDetail,
			getNikoPatient: async () => { await this.getNikoPatient(); this.reloadPatientTemplates(); }
		});
	}

	@action.bound
	showPreviousFlag(nikoPatientDetail) {
		return nikoPatientDetail && nikoPatientDetail.initialNikoPatientDetail && (nikoPatientDetail.status != nikoPatientDetail.initialNikoPatientDetail.Status);
	}

	@action.bound
	async getFieldHistory(item, fieldName, value) {
		if (item.history) {
			const history = (await this.historyService.getRecordHistory({ recordId: item.id, fieldName: fieldName, value: value })).data;
			item.history.userName = history.userName;
			item.history.eventDate = history.eventDate;
			item.history.fieldName = fieldName;
		}
	}
}
export default NikoPatientPreviewViewStore;